/**
 * RemixStudio — bottom drawer that's the heart of the iter-73 mobile flow.
 *
 *   ┌─────────────────────────────┐
 *   │ Remix Studio          [✕]   │
 *   │                              │
 *   │ [ Text ] [ Visual ] [ Full ] │   ← three mode chips
 *   │                              │
 *   │  TEXT ✓     VISUAL ✓         │   ← lock toggles (auto-set by mode)
 *   │                              │
 *   │  ┌─ Tones ──────────────┐    │
 *   │  │ auto · tender · bold │    │   (shown in TEXT + FULL)
 *   │  │ reflective · faith   │    │
 *   │  │ playful              │    │
 *   │  └──────────────────────┘    │
 *   │                              │
 *   │  earlier wordings (3) ▾      │   (VersionStepper, TEXT mode only)
 *   │                              │
 *   │  ┌─ Styles ─────────────┐    │
 *   │  │ horizontal rail      │    │   (shown in VISUAL + FULL)
 *   │  └──────────────────────┘    │
 *   │                              │
 *   │  [ Apply Full Remix ]        │   (FULL mode only — regenerates both)
 *   └─────────────────────────────┘
 *
 * The card stays visible above the drawer (drawer is ~70vh tall, leaves
 * the card peeking on top).
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Type, Image, Wand2, Loader2, Lock, Check } from "lucide-react";
import { tap } from "../../lib/haptic";
import { track } from "../../lib/track";
import VersionStepper from "../VersionStepper";
import MobileStyleRail from "./MobileStyleRail";

const TONES = ["auto", "tender", "bold", "reflective", "faith", "playful"];

const MODES = [
    { key: "text",   label: "Text",   icon: Type,  helper: "rewrites words · keeps look" },
    { key: "visual", label: "Visual", icon: Image, helper: "rewrites look · keeps words" },
    { key: "full",   label: "Full",   icon: Wand2, helper: "rewrites both" },
];

export default function RemixStudio({
    open,
    onClose,
    moodId,
    currentPack,
    currentTone,
    versions,
    isPremium,
    onRegenerateTone,
    onPackChange,
    onPremiumLocked,
    onReverted,
}) {
    const [mode, setMode] = useState("text");
    const [pendingTone, setPendingTone] = useState(null);

    const lockText = mode === "visual";   // VISUAL mode = text is locked
    const lockVisual = mode === "text";   // TEXT mode = visuals are locked

    const handleToneTap = async (tone) => {
        if (pendingTone) return;
        if (tone === currentTone || (tone === "auto" && !currentTone)) return;
        setPendingTone(tone);
        tap();
        try {
            await onRegenerateTone?.(tone === "auto" ? null : tone);
        } catch {
            /* parent toasts */
        } finally {
            setPendingTone(null);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={onClose}
                        className="fixed inset-0 z-40 bg-black/55 backdrop-blur-sm"
                        data-testid="remix-studio-backdrop"
                    />
                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed left-0 right-0 bottom-0 z-50 max-h-[78vh] pointer-events-none"
                        data-testid="remix-studio"
                    >
                        <div className="pointer-events-auto mx-auto w-full max-w-md rounded-t-3xl bg-[#0e0e11] border-t border-white/10 shadow-2xl overflow-y-auto max-h-[78vh]">
                            {/* Handle */}
                            <div className="pt-2.5 flex justify-center">
                                <div className="w-10 h-1 rounded-full bg-white/20" />
                            </div>

                            {/* Header */}
                            <div className="px-5 pt-3 pb-3 flex items-center justify-between">
                                <div>
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-violet-300/85">remix studio</div>
                                    <div className="font-display text-lg text-white/95 mt-0.5">sculpt the feeling</div>
                                </div>
                                <button
                                    onClick={onClose}
                                    aria-label="close"
                                    className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/85 transition-colors"
                                    data-testid="remix-studio-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Mode pills */}
                            <div className="px-4 pb-2 flex gap-2">
                                {MODES.map((m) => {
                                    const Icon = m.icon;
                                    const active = mode === m.key;
                                    return (
                                        <button
                                            key={m.key}
                                            onClick={() => { tap(); setMode(m.key); track("remix_mode_change", { mode: m.key }); }}
                                            className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-2xl border transition-all ${
                                                active
                                                    ? "bg-violet-400/15 border-violet-300/40 text-white"
                                                    : "bg-white/4 border-white/8 text-white/65 hover:bg-white/8"
                                            }`}
                                            data-testid={`remix-mode-${m.key}`}
                                        >
                                            <Icon className="w-4 h-4" strokeWidth={1.6} />
                                            <span className="text-[11px] font-medium tracking-wide">{m.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="px-4 pb-2">
                                <div className="text-[10px] tracking-[0.18em] uppercase text-white/40 text-center">
                                    {MODES.find((m) => m.key === mode)?.helper}
                                </div>
                            </div>

                            {/* Lock indicators */}
                            <div className="mx-4 my-2 rounded-xl bg-black/30 border border-white/6 px-3 py-2 flex items-center justify-around">
                                <LockIndicator label="text" locked={lockText} />
                                <span className="text-white/15">·</span>
                                <LockIndicator label="visual" locked={lockVisual} />
                            </div>

                            {/* TEXT controls — tones */}
                            {(mode === "text" || mode === "full") && (
                                <section className="px-4 pt-3" data-testid="remix-text-section">
                                    <div className="text-[10px] tracking-[0.22em] uppercase text-white/45 mb-2">
                                        tone of voice
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {TONES.map((t) => {
                                            const isCurrent =
                                                t === currentTone || (t === "auto" && !currentTone);
                                            const isBusy = pendingTone === t;
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    disabled={!!pendingTone}
                                                    onClick={() => handleToneTap(t)}
                                                    className={`px-3.5 py-1.5 rounded-full border text-xs tracking-wide transition-all ${
                                                        isCurrent
                                                            ? "bg-amber-400/20 border-amber-300/55 text-amber-100"
                                                            : "bg-white/4 border-white/10 text-white/75 hover:bg-white/8"
                                                    } ${isBusy ? "opacity-70" : ""}`}
                                                    data-testid={`remix-tone-${t}`}
                                                >
                                                    <span className="inline-flex items-center gap-1.5">
                                                        {isBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                                                        {isCurrent && !isBusy && <Check className="w-3 h-3" />}
                                                        <span className="lowercase font-serif italic normal-case">{t}</span>
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {/* Version history — only in TEXT mode (it's a wording concept) */}
                                    {mode === "text" && (
                                        <div className="mt-3">
                                            <VersionStepper
                                                moodId={moodId}
                                                versions={versions}
                                                onReverted={onReverted}
                                            />
                                        </div>
                                    )}
                                </section>
                            )}

                            {/* VISUAL controls — style rail */}
                            {(mode === "visual" || mode === "full") && (
                                <section className="pt-2 pb-2" data-testid="remix-visual-section">
                                    <MobileStyleRail
                                        moodId={moodId}
                                        currentPack={currentPack}
                                        isPremium={isPremium}
                                        onChange={onPackChange}
                                        onPremiumLocked={onPremiumLocked}
                                    />
                                </section>
                            )}

                            {/* Bottom action */}
                            <div className="px-4 pt-4 pb-6">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="w-full py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-medium text-sm tracking-wide transition-colors"
                                    data-testid="remix-studio-done"
                                >
                                    done
                                </button>
                            </div>
                            <div className="pb-[max(env(safe-area-inset-bottom),0rem)]" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

function LockIndicator({ label, locked }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className={`text-[10px] tracking-[0.22em] uppercase ${locked ? "text-white/40" : "text-amber-200/85"}`}>
                {label}
            </span>
            {locked ? (
                <Lock className="w-3 h-3 text-white/35" strokeWidth={1.8} />
            ) : (
                <Check className="w-3 h-3 text-amber-300" strokeWidth={2} />
            )}
        </div>
    );
}
